import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DeliveryAddressModal from "../components/address/DeliveryAddressModal";
import {
  STORAGE_DELIVERY_CITY,
  STORAGE_DELIVERY_COUNTRY,
  STORAGE_DELIVERY_HOUSENUMBER,
  STORAGE_DELIVERY_HOUSENUMBER_ADDITION,
  STORAGE_DELIVERY_STREET,
  STORAGE_DELIVERY_ZIP,
} from "../constants/LocalStorageConstants";
import { COUNTRY_NL } from "../constants/CountryConstants";
import { isLandelijk } from "../helpers/region";
import { useAuthContext } from "../context/AuthContext";

const DeliveryAddressContext = createContext({
  open: false,
  openModal: async () => ({}),
  closeModal: () => {},
  values: {
    country: COUNTRY_NL,
    zip: "",
    houseNumber: "",
    houseNumberAddition: "",
    street: "",
    city: "",
    landelijk: false,
  },
  setValues: () => {},
});

export const DeliveryAddressProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({
    country: COUNTRY_NL,
    zip: "",
    houseNumber: "",
    houseNumberAddition: "",
    street: "",
    city: "",
    landelijk: false,
  });
  const { userProfile } = useAuthContext();

  // Promise resolver to notify caller when user completes step 1 (onSaved)
  const resolverRef = useRef(null);

  // Load initial values from localStorage on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const country =
      localStorage.getItem(STORAGE_DELIVERY_COUNTRY) || COUNTRY_NL;
    const zip = localStorage.getItem(STORAGE_DELIVERY_ZIP) || "";
    const houseNumber =
      localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER) || "";
    const houseNumberAddition =
      localStorage.getItem(STORAGE_DELIVERY_HOUSENUMBER_ADDITION) || "";
    const street = localStorage.getItem(STORAGE_DELIVERY_STREET) || "";
    const city = localStorage.getItem(STORAGE_DELIVERY_CITY) || "";

    setValues((prev) => ({
      ...prev,
      country,
      zip,
      houseNumber,
      houseNumberAddition,
      street,
      city,
      landelijk: country === COUNTRY_NL ? isLandelijk(zip) : true,
    }));
  }, []);

  // When a logged-in user has a delivery address in their profile, auto-fill values
  useEffect(() => {
    const delivery = userProfile?.address?.delivery;
    if (!delivery) return;
    const country = delivery.country || COUNTRY_NL;
    const zip = delivery.zipCode || "";
    const houseNumber = delivery.houseNumber || "";
    const houseNumberAddition = delivery.addition || "";
    const street = delivery.street || "";
    const city = delivery.city || "";
    const landelijk = country === COUNTRY_NL ? isLandelijk(zip) : true;

    setValues((prev) => ({
      ...prev,
      country,
      zip,
      houseNumber,
      houseNumberAddition,
      street,
      city,
      landelijk,
    }));
  }, [userProfile]);

  const openModal = useCallback(() => {
    setOpen(true);
    // return a promise that resolves when user completes step 1 (onSaved)
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSaved = useCallback(
    (
      country,
      zip,
      houseNumber,
      street = "",
      city = "",
      houseNumberAddition = "",
    ) => {
      const landelijk = country === COUNTRY_NL ? isLandelijk(zip) : true;
      setValues((prev) => ({
        ...prev,
        country,
        zip,
        houseNumber,
        houseNumberAddition,
        street,
        city,
        landelijk,
      }));
      if (resolverRef.current) {
        try {
          resolverRef.current({
            country,
            zip,
            houseNumber,
            houseNumberAddition,
            street,
            city,
            landelijk,
          });
        } finally {
          resolverRef.current = null;
        }
      }
      // Do not close the modal here; it proceeds to step 2 inside the modal
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      open,
      openModal,
      closeModal,
      values,
      setValues,
    }),
    [open, openModal, closeModal, values],
  );

  return (
    <DeliveryAddressContext.Provider value={contextValue}>
      {children}
      <DeliveryAddressModal
        open={open}
        onClose={closeModal}
        onSaved={handleSaved}
      />
    </DeliveryAddressContext.Provider>
  );
};

export const useDeliveryAddress = () => useContext(DeliveryAddressContext);
